import { Solution } from '../types/solution';
import { FilterOption } from '../types/common';
import { LambdaSolutionListItem, LambdaSolutionDetail } from '../types/lambda';
import { invokeLambdaWithRetry } from './lambdaClient';

// Map Lambda solution list item to Solution interface
const mapLambdaSolutionToSolution = (lambdaSolution: LambdaSolutionListItem): Solution => {
  // Extract all business functions (comma-separated)
  const businessFunctions = lambdaSolution.businessFunction 
    ? lambdaSolution.businessFunction.split(',').map(fn => fn.trim()).filter(fn => fn.length > 0)
    : [];

  return {
    id: lambdaSolution.eventId,
    title: lambdaSolution.name,
    summary: lambdaSolution.shortDescription,
    industry: lambdaSolution.industry as any, // Type assertion for now
    valueProposition: '', // Not available in list view
    velocityEstimation: '', // Not available in list view
    arrEstimation: '', // Not available in list view
    demoVideo: lambdaSolution.displayPhoto ? {
      url: lambdaSolution.displayPhoto,
      altText: lambdaSolution.name,
      type: 'image' as const
    } : undefined,
    architectureDiagram: undefined,
    awsServices: [],
    references: [],
    relatedGTMInitiatives: [],
    tags: businessFunctions,
    status: lambdaSolution.status ? 'active' : 'draft' as any,
    createdAt: new Date(),
    updatedAt: new Date(),
    businessFunction: lambdaSolution.businessFunction
  };
};

// Map Lambda solution detail to Solution interface
const mapLambdaDetailToSolution = (lambdaDetail: LambdaSolutionDetail): Solution => {
  // Get video URL from urls.demoVideo array
  const videoUrl = lambdaDetail.urls?.demoVideo?.[0];
  
  // Get architecture diagram from urls.technicalArchitecture array
  const architectureUrl = lambdaDetail.urls?.technicalArchitecture?.[0];

  // Extract all business functions (comma-separated)
  const businessFunctions = lambdaDetail.businessFunction 
    ? lambdaDetail.businessFunction.split(',').map(fn => fn.trim()).filter(fn => fn.length > 0)
    : [];
  
  // Parse lastUpdate from Lambda API
  const lastUpdate = lambdaDetail.lastUpdate ? new Date(lambdaDetail.lastUpdate) : undefined;
  
  return {
    id: lambdaDetail.eventId,
    title: lambdaDetail.name,
    summary: lambdaDetail.shortDescription,
    industry: lambdaDetail.industry as any,
    valueProposition: lambdaDetail.description || '',
    velocityEstimation: '', // Not available in API
    arrEstimation: '', // Not available in API
    demoVideo: videoUrl ? {
      url: videoUrl,
      altText: `${lambdaDetail.name} Demo Video`,
      type: 'video' as const
    } : undefined,
    architectureDiagram: architectureUrl ? {
      url: architectureUrl,
      altText: `${lambdaDetail.name} Architecture`,
      type: 'image' as const
    } : undefined,
    awsServices: lambdaDetail.techArchitecture ? [{
      name: 'Architecture',
      category: 'Architecture',
      description: lambdaDetail.techArchitecture
    }] : [],
    references: [],
    relatedGTMInitiatives: [],
    tags: businessFunctions,
    status: lambdaDetail.status ? 'active' : 'draft' as any,
    createdAt: new Date(),
    updatedAt: new Date(),
    lastUpdate,
    // Additional Lambda fields
    benefits: lambdaDetail.benefits,
    solutionFlow: lambdaDetail.solutionFlow,
    industryAdoption: lambdaDetail.industryAdoption,
    businessFunction: lambdaDetail.businessFunction,
    techArchitecture: lambdaDetail.techArchitecture,
    // Raw URLs from Lambda API
    urls: lambdaDetail.urls
  };
};

export class SolutionService {
  // Get all solutions from Lambda API
  static async getAllSolutions(): Promise<Solution[]> {
    try {
      const response = await invokeLambdaWithRetry<LambdaSolutionListItem[]>({
        action: 'getAllSolutions'
      });

      if (response.success && response.data) {
        // Return all solutions, don't filter by status
        return response.data.map(mapLambdaSolutionToSolution);
      }

      console.error('Failed to fetch solutions:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching all solutions:', error);
      return [];
    }
  }

  // Get solution by ID from Lambda API
  static async getSolutionById(id: string): Promise<Solution | null> {
    try {
      const response = await invokeLambdaWithRetry<LambdaSolutionDetail>({
        action: 'getSolution',
        payload: { eventId: id }
      });

      if (response.success && response.data) {
        return mapLambdaDetailToSolution(response.data);
      }

      console.error('Failed to fetch solution:', response.error);
      return null;
    } catch (error) {
      console.error('Error fetching solution by ID:', error);
      return null;
    }
  }

  // Get solutions by IDs
  static async getSolutionsByIds(ids: string[]): Promise<Solution[]> {
    const allSolutions = await this.getAllSolutions();
    return allSolutions.filter((solution: Solution) => ids.includes(solution.id));
  }

  // Get solutions by GTM initiative ID
  static async getSolutionsByGTMId(gtmId: string): Promise<Solution[]> {
    const allSolutions = await this.getAllSolutions();
    return allSolutions.filter((solution: Solution) => 
      solution.relatedGTMInitiatives.includes(gtmId)
    );
  }

  // Get filtered solutions
  static async getFilteredSolutions(filters: {
    industry?: string[];
    awsService?: string[];
    tags?: string[];
    search?: string;
  }): Promise<Solution[]> {
    const allSolutions = await this.getAllSolutions();
    let filteredSolutions = allSolutions;

    // Filter by industry
    if (filters.industry && filters.industry.length > 0) {
      filteredSolutions = filteredSolutions.filter((solution: Solution) =>
        filters.industry!.includes(solution.industry)
      );
    }

    // Filter by AWS services
    if (filters.awsService && filters.awsService.length > 0) {
      filteredSolutions = filteredSolutions.filter((solution: Solution) =>
        solution.awsServices.some((service: any) =>
          filters.awsService!.includes(service.name)
        )
      );
    }

    // Filter by tags
    if (filters.tags && filters.tags.length > 0) {
      filteredSolutions = filteredSolutions.filter((solution: Solution) =>
        solution.tags.some((tag: string) =>
          filters.tags!.includes(tag)
        )
      );
    }

    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredSolutions = filteredSolutions.filter((solution: Solution) =>
        solution.title.toLowerCase().includes(searchTerm) ||
        solution.summary.toLowerCase().includes(searchTerm) ||
        solution.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm))
      );
    }

    return filteredSolutions;
  }

  // Get filter options for solutions
  static getFilterOptions(solutions: Solution[]): FilterOption[] {
    // Get unique industries
    const industriesSet = new Set<string>();
    for (const s of solutions) {
      industriesSet.add(s.industry);
    }
    const industries = Array.from(industriesSet);
    
    // Define industry groups with dividers
    const industryGroups = [
      // Group 1: Horizontal (top)
      ['Horizontal'],
      // Group 2: Financial sector
      ['Banking', 'Insurance', 'Financial Services'],
      // Group 3: Manufacturing
      ['Manufacturing', 'Automotive'],
      // Group 4: Everything else (alphabetically sorted)
      ['Education', 'Energy and Utilities', 'Life Sciences and Healthcare', 'Telecom, Media and Entertainment']
    ];

    // Build ordered industry list with dividers
    const orderedIndustries: (string | { divider: number })[] = [];
    
    industryGroups.forEach((group, groupIndex) => {
      // Filter to only include industries that exist in the data
      const existingInGroup = group.filter(ind => industries.includes(ind));
      
      if (existingInGroup.length > 0) {
        // Add industries from this group
        orderedIndustries.push(...existingInGroup);
        
        // Add divider after each group except the last one
        if (groupIndex < industryGroups.length - 1) {
          orderedIndustries.push({ divider: groupIndex + 1 });
        }
      }
    });

    // Add any industries not in the predefined groups (alphabetically)
    const allDefinedIndustries = new Set(industryGroups.flat());
    const undefinedIndustries = industries
      .filter(ind => !allDefinedIndustries.has(ind))
      .sort((a, b) => a.localeCompare(b));
    
    if (undefinedIndustries.length > 0) {
      // Add divider before undefined industries if there are any ordered industries
      if (orderedIndustries.length > 0) {
        orderedIndustries.push({ divider: industryGroups.length });
      }
      orderedIndustries.push(...undefinedIndustries);
    }
    
    // Get unique AWS services
    const awsServicesSet = new Set<string>();
    for (const s of solutions) {
      for (const aws of s.awsServices) {
        awsServicesSet.add(aws.name);
      }
    }
    const awsServices = Array.from(awsServicesSet);
    
    // Get unique tags (business functions) and sort alphabetically
    const tagsSet = new Set<string>();
    for (const s of solutions) {
      for (const tag of s.tags) {
        if (tag && tag.trim().length > 0) {
          tagsSet.add(tag);
        }
      }
    }
    const tags = Array.from(tagsSet).sort((a, b) => a.localeCompare(b));

    const filterOptions: FilterOption[] = [
      ...orderedIndustries.map((item, index) => {
        if (typeof item === 'object' && 'divider' in item) {
          // Create a divider entry
          return {
            id: `industry-divider-${item.divider}`,
            label: '',
            category: 'industry' as const,
            count: 0
          };
        } else {
          // Regular industry filter
          return {
            id: `industry-${item}`,
            label: item,
            category: 'industry' as const,
            count: solutions.filter((s: Solution) => s.industry === item).length
          };
        }
      }),
      ...awsServices.map(service => ({
        id: `aws-${service}`,
        label: service,
        category: 'awsService' as const,
        count: solutions.filter((s: Solution) => s.awsServices.some((aws: any) => aws.name === service)).length
      })),
      ...tags.map(tag => ({
        id: `tag-${tag}`,
        label: tag,
        category: 'solutionType' as const,
        count: solutions.filter((s: Solution) => s.tags.includes(tag)).length
      }))
    ];

    return filterOptions;
  }

  // Get featured solutions for home page
  static async getFeaturedSolutions(limit: number = 4): Promise<Solution[]> {
    const allSolutions = await this.getAllSolutions();
    return allSolutions.slice(0, limit);
  }
}